import { RecentSearchItem, SearchResultItem } from "../types";

// // // //

export const RECENT_SEARCHES_STORAGE_KEY = "search-recent-destinations:v1";
export const RECENT_SEARCHES_SYNC_EVENT = "search-recent-destinations:update";
export const MAX_RECENT_SEARCHES = 5;

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

// // // //

/**
 * Get the storage object
 */
function getStorage(): StorageLike | null {
    if (typeof window === "undefined") {
        return null;
    }

    try {
        return window.localStorage;
    } catch {
        return null;
    }
}

/**
 * Check if a value is a recent search item
 */
function isRecentSearchItem(value: unknown): value is RecentSearchItem {
    if (!value || typeof value !== "object") {
        return false;
    }

    const candidate = value as Record<string, unknown>;

    return (
        typeof candidate.href === "string" &&
        candidate.href !== "" &&
        typeof candidate.label === "string" &&
        candidate.label !== "" &&
        typeof candidate.category === "string" &&
        candidate.category !== "" &&
        (candidate.subtitle === undefined ||
            typeof candidate.subtitle === "string") &&
        (candidate.icon === undefined || typeof candidate.icon === "string") &&
        (candidate.iconClassName === undefined ||
            typeof candidate.iconClassName === "string")
    );
}

/**
 * Normalize the recent searches
 */
export function normalizeRecentSearches(
    items: unknown,
    limit = MAX_RECENT_SEARCHES,
): RecentSearchItem[] {
    if (!Array.isArray(items)) {
        return [];
    }

    const seenHrefs = new Set<string>();
    const normalized: RecentSearchItem[] = [];

    for (const item of items) {
        if (!isRecentSearchItem(item) || seenHrefs.has(item.href)) {
            continue;
        }

        seenHrefs.add(item.href);
        normalized.push({
            href: item.href,
            label: item.label,
            subtitle: item.subtitle,
            icon: item.icon,
            iconClassName: item.iconClassName,
            iconWrapperClassName: item.iconWrapperClassName,
            category: item.category,
        });

        if (normalized.length >= limit) {
            break;
        }
    }

    return normalized;
}

/**
 * Add a recent search to the list
 */
export function addRecentSearch(
    currentItems: RecentSearchItem[],
    nextItem: RecentSearchItem,
    limit = MAX_RECENT_SEARCHES,
): RecentSearchItem[] {
    return normalizeRecentSearches(
        [
            nextItem,
            ...currentItems.filter((item) => item.href !== nextItem.href),
        ],
        limit,
    );
}

/**
 * Read the recent searches from storage
 */
export function readRecentSearches(
    storage: StorageLike | null = getStorage(),
): RecentSearchItem[] {
    if (!storage) {
        return [];
    }

    try {
        const storedValue = storage.getItem(RECENT_SEARCHES_STORAGE_KEY);

        if (!storedValue) {
            return [];
        }

        return normalizeRecentSearches(JSON.parse(storedValue));
    } catch {
        return [];
    }
}

/**
 * Write the recent searches to storage
 */
export function writeRecentSearches(
    items: RecentSearchItem[],
    storage: StorageLike | null = getStorage(),
) {
    if (!storage) {
        return;
    }

    storage.setItem(
        RECENT_SEARCHES_STORAGE_KEY,
        JSON.stringify(normalizeRecentSearches(items)),
    );
}

/**
 * Clear the recent searches from storage
 */
export function clearStoredRecentSearches(
    storage: StorageLike | null = getStorage(),
) {
    storage?.removeItem(RECENT_SEARCHES_STORAGE_KEY);
}

/**
 * Convert a search result item to a recent search item
 */
export function toRecentSearchItem(item: SearchResultItem): RecentSearchItem {
    return {
        href: item.href,
        label: item.content.label,
        subtitle: item.content.subtitle,
        icon: item.content.icon,
        iconClassName: item.content.iconClassName,
        iconWrapperClassName: item.content.iconWrapperClassName,
        category: item.category,
    };
}

/**
 * Convert a recent search item to a search result item
 */
export function toRecentSearchResultItem(
    item: RecentSearchItem,
): SearchResultItem {
    return {
        id: `recent-search-${item.href}`,
        href: item.href,
        category: item.category,
        content: {
            label: item.label,
            subtitle: item.subtitle ?? item.category,
            icon: item.icon,
            iconClassName: item.iconClassName,
            iconWrapperClassName: item.iconWrapperClassName,
        },
    };
}
